import { applySaveOrderDateFields, formatLocalDate, toInputDate } from './dateLocal';
import { toFiniteNumber } from './numbers';

/** Optional FK: null when not selected (do not send 0). */
export function optionalOrderId(value) {
  if (value === null || value === undefined || value === '') return null;
  const id = parseInt(value, 10);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/** Normalize order loaded from OrderById for edit/save (same shape as Create Order form). */
export function normalizeOrderFromApi(orderData) {
  if (!orderData) return null;
  return {
    ...orderData,
    PickUpById: orderData.PickUpById ?? '',
    AgentId: orderData.AgentId ?? '',
    TypeId: orderData.TypeId > 0 ? orderData.TypeId : '',
    PickupDate: orderData.PickupDate ? toInputDate(orderData.PickupDate) : formatLocalDate(),
    DeliveryDate: orderData.DeliveryDate ? toInputDate(orderData.DeliveryDate) : formatLocalDate(),
    TempServiceCharge: orderData.TempServiceCharge ?? orderData.DeliveryCharge ?? 0,
    delchargechanged: false,
  };
}

/** Payment/total calculation — mirrors Create Order `calculateTotals`. */
export function calculateOrderPaymentTotals(order, companyProfile) {
  if (!order || !companyProfile) return order;

  let updatedOrder = {
    ...order,
    ProductPrice: toFiniteNumber(order.ProductPrice),
    DeliveryCharge: toFiniteNumber(order.DeliveryCharge),
    TempServiceCharge: toFiniteNumber(order.TempServiceCharge ?? order.DeliveryCharge),
    TAX: toFiniteNumber(order.TAX),
  };

  if (updatedOrder.Id > 0 && updatedOrder.delchargechanged === false && companyProfile.IsVATFromDelivery) {
    // Keep existing delivery charge
  } else {
    updatedOrder.DeliveryCharge = updatedOrder.TempServiceCharge;
  }

  if (companyProfile.DefTAX > 0) {
    let tdel = updatedOrder.DeliveryCharge + updatedOrder.TAX;

    if (updatedOrder.delchargechanged === true) {
      tdel = updatedOrder.DeliveryCharge;
    } else {
      tdel = updatedOrder.DeliveryCharge + updatedOrder.TAX;
    }

    if (companyProfile.IsVATFromDelivery) {
      if (
        updatedOrder.Id > 0 &&
        (updatedOrder.delchargechanged === false || updatedOrder.delchargechanged === undefined)
      ) {
        updatedOrder.TAX = (tdel * companyProfile.DefTAX) / (100 + companyProfile.DefTAX);
      } else {
        updatedOrder.TAX = (updatedOrder.DeliveryCharge * companyProfile.DefTAX) / (100 + companyProfile.DefTAX);
      }
      updatedOrder.TAX = Math.round(updatedOrder.TAX * 100) / 100;

      if (
        updatedOrder.Id > 0 &&
        (updatedOrder.delchargechanged === false || updatedOrder.delchargechanged === undefined)
      ) {
        updatedOrder.DeliveryCharge = tdel - updatedOrder.TAX;
      } else {
        updatedOrder.DeliveryCharge = updatedOrder.DeliveryCharge - updatedOrder.TAX;
      }
    } else {
      updatedOrder.TAX = updatedOrder.DeliveryCharge * (companyProfile.DefTAX / 100);
      updatedOrder.TAX = Math.round(updatedOrder.TAX * 100) / 100;
    }

    updatedOrder.delchargechanged = false;
  }

  updatedOrder.IsFree = false;
  updatedOrder.IsAdvancePay = false;
  updatedOrder.IsAdvancePrice = false;

  if (updatedOrder.PayType === 'FOC') {
    updatedOrder.IsFree = true;
    updatedOrder.DeliveryCharge = 0;
    updatedOrder.TAX = 0;
  }

  if (updatedOrder.PayType === 'CAO' || updatedOrder.PayType === 'TAO') {
    updatedOrder.IsAdvancePay = true;
  }

  if (updatedOrder.PayType === 'APS' || updatedOrder.PayType === 'APR') {
    updatedOrder.IsAdvancePrice = true;
  }

  if (updatedOrder.PayType === 'CAD') {
    if (companyProfile.IsVATFromCustomerOnly) {
      updatedOrder.CustomerAmount = updatedOrder.ProductPrice + updatedOrder.TAX;
      updatedOrder.ClientAmount = updatedOrder.ProductPrice - updatedOrder.DeliveryCharge;
    } else {
      updatedOrder.CustomerAmount = updatedOrder.ProductPrice;
      updatedOrder.ClientAmount = updatedOrder.ProductPrice - (updatedOrder.DeliveryCharge + updatedOrder.TAX);
    }
  } else if (updatedOrder.PayType === 'COD') {
    updatedOrder.CustomerAmount = updatedOrder.ProductPrice + updatedOrder.DeliveryCharge + updatedOrder.TAX;
    updatedOrder.ClientAmount = updatedOrder.ProductPrice;
  } else if (updatedOrder.PayType === 'CAO') {
    updatedOrder.CustomerAmount = updatedOrder.ProductPrice;
    updatedOrder.ClientAmount = updatedOrder.ProductPrice;
  } else if (updatedOrder.PayType === 'TAO') {
    updatedOrder.CustomerAmount = updatedOrder.ProductPrice;
    updatedOrder.ClientAmount = updatedOrder.ProductPrice;
  } else if (updatedOrder.PayType === 'FOC') {
    updatedOrder.CustomerAmount = updatedOrder.ProductPrice;
    updatedOrder.ClientAmount = updatedOrder.ProductPrice;
  } else if (updatedOrder.PayType === 'APS') {
    if (companyProfile.IsVATFromCustomerOnly) {
      updatedOrder.CustomerAmount = updatedOrder.ProductPrice + updatedOrder.TAX;
      updatedOrder.ClientAmount = 0 - (updatedOrder.ProductPrice - updatedOrder.DeliveryCharge);
    } else {
      updatedOrder.CustomerAmount = updatedOrder.ProductPrice;
      updatedOrder.ClientAmount = 0 - (updatedOrder.ProductPrice - (updatedOrder.DeliveryCharge + updatedOrder.TAX));
    }
  } else if (updatedOrder.PayType === 'APR') {
    updatedOrder.CustomerAmount = updatedOrder.ProductPrice + updatedOrder.DeliveryCharge + updatedOrder.TAX;
    updatedOrder.ClientAmount = 0 - updatedOrder.ProductPrice;
  }

  updatedOrder.CustomerAmount = Math.round(updatedOrder.CustomerAmount * 100) / 100;
  updatedOrder.ClientAmount = Math.round(updatedOrder.ClientAmount * 100) / 100;

  return updatedOrder;
}

/** Recalculate payment fields for admin payment edit (amounts + tax from company rules). */
export function recalculateAdminPaymentFields(prev, updates, companyProfile) {
  const deliveryRaw =
    updates.DeliveryCharge !== undefined ? updates.DeliveryCharge : prev.DeliveryCharge;
  // Keep interim typing values ('', '-') out of calc until a real number is entered
  if (deliveryRaw === '' || deliveryRaw === '-' || deliveryRaw === '.' || deliveryRaw === '-.') {
    return {
      ...prev,
      ...updates,
      DeliveryCharge: deliveryRaw,
      TempServiceCharge: deliveryRaw,
      delchargechanged: true,
    };
  }
  if (
    updates.ProductPrice === '' ||
    updates.ProductPrice === '-' ||
    updates.ProductPrice === '.' ||
    updates.ProductPrice === '-.'
  ) {
    return {
      ...prev,
      ...updates,
      delchargechanged: true,
    };
  }

  const deliveryCharge = toFiniteNumber(deliveryRaw);

  const merged = {
    ...prev,
    ...updates,
    Id: prev.Id || updates.Id,
    ProductPrice: toFiniteNumber(
      updates.ProductPrice !== undefined ? updates.ProductPrice : prev.ProductPrice
    ),
    TempServiceCharge: deliveryCharge,
    delchargechanged: true,
  };

  const calculated = calculateOrderPaymentTotals(merged, companyProfile || {});
  return {
    ...prev,
    ...updates,
    ProductPrice: calculated.ProductPrice,
    DeliveryCharge: calculated.DeliveryCharge,
    TAX: calculated.TAX,
    ClientAmount: calculated.ClientAmount,
    CustomerAmount: calculated.CustomerAmount,
    TempServiceCharge: calculated.TempServiceCharge ?? calculated.DeliveryCharge,
    delchargechanged: calculated.delchargechanged ?? false,
  };
}

/** Build SaveOrder API payload from a loaded order (nested Client/Customer included). */
export function buildSaveOrderPayload(order) {
  const withDates = applySaveOrderDateFields(order);
  const client = withDates.Client || {};
  const customer = withDates.Customer || {};
  const clientAddress = withDates.ClientAddress || {};
  const customerAddress = withDates.CustomerAddress || {};

  return {
    ...withDates,
    ProductPrice: toFiniteNumber(withDates.ProductPrice),
    DeliveryCharge: toFiniteNumber(withDates.DeliveryCharge),
    TempServiceCharge: toFiniteNumber(withDates.TempServiceCharge ?? withDates.DeliveryCharge),
    TAX: toFiniteNumber(withDates.TAX),
    ClientAmount: toFiniteNumber(withDates.ClientAmount),
    CustomerAmount: toFiniteNumber(withDates.CustomerAmount),
    ClientId: parseInt(withDates.ClientId, 10) || 0,
    CustomerId: parseInt(withDates.CustomerId, 10) || 0,
    ClientAddressId: parseInt(withDates.ClientAddressId, 10) || 0,
    CustomerAddressId: parseInt(withDates.CustomerAddressId, 10) || 0,
    PickUpById: optionalOrderId(withDates.PickUpById),
    AgentId: optionalOrderId(withDates.AgentId),
    TypeId: optionalOrderId(withDates.TypeId),
    Client: { ...client },
    ClientAddress: {
      ...clientAddress,
      EmirateId: parseInt(clientAddress.EmirateId, 10) || 0,
      LocationId: parseInt(clientAddress.LocationId, 10) || 0,
    },
    Customer: { ...customer },
    CustomerAddress: {
      ...customerAddress,
      EmirateId: parseInt(customerAddress.EmirateId, 10) || 0,
      LocationId: parseInt(customerAddress.LocationId, 10) || 0,
    },
  };
}

export function isSaveOrderSuccess(response) {
  if (response?.Status === 'Success') return true;
  return response === true || response === 'true' || response === 'Success';
}
